import { zonedDate } from '../../../utils/date-time';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useAppContext } from '../../../contexts/AppContext';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { Modal } from '../../../components/Modal';
import { Drawer } from '../../../components/Drawer';
import { baseURL } from '../../../AxiosConfig';
import { ServicosService } from '../../servicos/Service';
import { AtendentesService } from '../../atendentes/Service';
import { HorariosAtendenteService } from '../../agenda/Service';
import { AgendaClienteService } from '../../agenda/cliente/Service';
import { RecorrenciasService } from '../../recorrencias/Service';
import { formatCivilDateBR } from './utils/periodosUtils';
import { DurationService } from '../../servicos/list/Duration';
import { describeRegra, formatProximo } from '../../recorrencias/helpers';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { ChevronLeft, ChevronRight, Plus, RefreshCw, Scissors, Trash2 } from 'lucide-react';

type RecorrenciaTipo = 'semanas' | 'meses';
type DiaSemana =
    | 'domingo'
    | 'segunda'
    | 'terca'
    | 'quarta'
    | 'quinta'
    | 'sexta'
    | 'sabado';
type OcorrenciaMensal = 'primeira' | 'segunda' | 'terceira' | 'quarta' | 'ultima';

type PlanoService = {
    serviceId: string;
    recurrence?: string;
    quantity?: number;
};

type AssinaturaPlanConfig = {
    periodicity?: string;
    quantity?: number;
    indeterminate?: boolean;
};

type ServicoInfo = {
    name?: string;
    duration?: number;
};

type RecorrenciaGroup = {
    key: string;
    recurrence: 'semanal' | 'mensal';
    quantity: number;
    defaultFrequency: number;
    frequencyTipo: RecorrenciaTipo;
    serviceIds: string[];
    mainServiceId: string;
    extrasIds: string[];
    serviceNames: string[];
    totalDuration: number;
};

type RecorrenciaGroupCardHandle = {
    create: () => Promise<boolean>;
    isReady: () => boolean;
};

const servicosService = new ServicosService();
const atendentesService = new AtendentesService();
const horariosAtendenteService = new HorariosAtendenteService();
const agendaClienteService = new AgendaClienteService();
const recorrenciasService = new RecorrenciasService();

const diasSemana = [
    'domingo',
    'segunda',
    'terca',
    'quarta',
    'quinta',
    'sexta',
    'sabado',
] as const;

const diasSemanaOptions: Array<{ value: DiaSemana; label: string }> = [
    { value: 'segunda', label: 'segunda-feira' },
    { value: 'terca', label: 'terça-feira' },
    { value: 'quarta', label: 'quarta-feira' },
    { value: 'quinta', label: 'quinta-feira' },
    { value: 'sexta', label: 'sexta-feira' },
    { value: 'sabado', label: 'sábado' },
    { value: 'domingo', label: 'domingo' },
];

const ocorrenciasMensaisOptions: Array<{ value: OcorrenciaMensal; label: string }> = [
    { value: 'primeira', label: 'Primeira' },
    { value: 'segunda', label: 'Segunda' },
    { value: 'terceira', label: 'Terceira' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'ultima', label: 'Última' },
];


const normalizeRecurrence = (value?: string) => {
    const rec = (value || 'mensal').toLowerCase();
    return rec === 'semanal' ? 'semanal' : 'mensal';
};

const resolveAssinaturaPlanConfig = (assinatura: any): AssinaturaPlanConfig => {
    const plan =
        assinatura?.planoSnapshot ||
        assinatura?.plano ||
        assinatura?.plan ||
        {};

    const quantity =
        typeof plan?.quantity === 'number'
            ? plan.quantity
            : typeof assinatura?.quantity === 'number'
                ? assinatura.quantity
                : undefined;

    const indeterminate = Boolean(
        plan?.indeterminateQuantity ??
            assinatura?.indeterminateQuantity ??
            false,
    );

    return {
        periodicity: plan?.periodicity || assinatura?.periodicity,
        quantity,
        indeterminate,
    };
};

const computeEndAtFromConfig = (
    startAt?: string | Date | null,
    config?: AssinaturaPlanConfig,
    timezone?: string,
): Date | null => {
    if (!startAt || !config?.periodicity) return null;
    const quantity = Number(config.quantity || 0);
    if (!quantity || config.indeterminate) return null;

    const date = zonedDate(startAt, timezone);
    if (!date.isValid()) return null;

    switch (config.periodicity) {
        case 'weekly':
            date.add(quantity, 'weeks');
            break;
        case 'biweekly':
            date.add(2 * quantity, 'weeks');
            break;
        case 'monthly':
            date.add(quantity, 'months');
            break;
        case 'bimonthly':
            date.add(2 * quantity, 'months');
            break;
        case 'quarterly':
            date.add(3 * quantity, 'months');
            break;
        case 'biannual':
            date.add(6 * quantity, 'months');
            break;
        case 'yearly':
            date.add(quantity, 'years');
            break;
        default:
            return null;
    }

    return date.toDate();
};

const getDiaSemana = (value?: string) => {
    if (!value) return '';
    const dayIndex = moment(value).day();
    return diasSemana[dayIndex] || '';
};

const diaSemanaFromDate = (value?: string | Date | moment.Moment): DiaSemana => {
    const date = moment(value).isValid() ? moment(value) : moment();
    return diasSemana[date.day()];
};

const ocorrenciaMensalFromDate = (value?: string | Date | moment.Moment): OcorrenciaMensal => {
    const date = moment(value).isValid() ? moment(value) : moment();

    if (date.clone().add(7, 'days').month() !== date.month()) {
        return 'ultima';
    }

    const occurrence = Math.ceil(date.date() / 7);
    if (occurrence <= 1) return 'primeira';
    if (occurrence === 2) return 'segunda';
    if (occurrence === 3) return 'terceira';
    return 'quarta';
};

const RecorrenciaGroupCard = forwardRef<RecorrenciaGroupCardHandle, {
    group: RecorrenciaGroup;
    assinaturaId?: string;
    clientId?: string;
    estabId?: string;
    defaultStartAt?: string;
    defaultEndAt?: string;
    onCreated: () => void;
    showCreateButton?: boolean;
}>(({
    group,
    assinaturaId,
    clientId,
    estabId,
    defaultStartAt,
    defaultEndAt,
    onCreated,
    showCreateButton = true,
}, ref) => {
    const { errorSnackbar, successSnackbar } = useAppContext();
    const isStartLocked = false;
    const isEndLocked = Boolean(defaultEndAt);
    const tipo: RecorrenciaTipo = group.frequencyTipo;
    const [attendants, setAttendants] = useState<any[]>([]);
    const [attendantId, setAttendantId] = useState('');
    const [selectedDate, setSelectedDate] = useState(defaultStartAt || '');
    const [endDate, setEndDate] = useState(defaultEndAt || '');
    const [horarios, setHorarios] = useState<number[]>([]);
    const [selectedTime, setSelectedTime] = useState<number | null>(null);
    const [loadingAttendants, setLoadingAttendants] = useState(false);
    const [loadingTimes, setLoadingTimes] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preferredWeekday, setPreferredWeekday] = useState('');
    const [preferredWeekdayTouched, setPreferredWeekdayTouched] = useState(false);
    const [monthlyWeekday, setMonthlyWeekday] = useState<DiaSemana>(() =>
        diaSemanaFromDate(defaultStartAt || new Date()),
    );
    const [monthlyOccurrence, setMonthlyOccurrence] = useState<OcorrenciaMensal>(() =>
        ocorrenciaMensalFromDate(defaultStartAt || new Date()),
    );
    const [monthlyRuleTouched, setMonthlyRuleTouched] = useState(false);
    const frequencia = group.defaultFrequency || 1;
    const effectiveStartDate = selectedDate || defaultStartAt || '';
    const diaSemana = useMemo(
        () => getDiaSemana(effectiveStartDate),
        [effectiveStartDate],
    );
    const weekDays = useMemo(() => {
        const base = effectiveStartDate
            ? moment(effectiveStartDate).startOf('week')
            : moment().startOf('week');
        return Array.from({ length: 7 }, (_, idx) =>
            base.clone().add(idx, 'day'),
        );
    }, [effectiveStartDate]);
    const formattedDate = useMemo(() => {
        if (!effectiveStartDate) return '-';
        return moment(effectiveStartDate).format('dddd, DD [de] MMMM [de] YYYY');
    }, [effectiveStartDate]);

    useEffect(() => {
        if (defaultStartAt) {
            setSelectedDate(defaultStartAt);
        }
    }, [defaultStartAt]);

    useEffect(() => {
        setEndDate(defaultEndAt || '');
    }, [defaultEndAt]);

    useEffect(() => {
        if (!preferredWeekdayTouched && diaSemana) {
            setPreferredWeekday(diaSemana);
        }
    }, [preferredWeekdayTouched, diaSemana]);

    useEffect(() => {
        if (tipo !== 'meses' || monthlyRuleTouched) return;

        setMonthlyWeekday(diaSemanaFromDate(effectiveStartDate));
        setMonthlyOccurrence(ocorrenciaMensalFromDate(effectiveStartDate));
    }, [effectiveStartDate, monthlyRuleTouched, tipo]);

    useEffect(() => {
        if (!estabId || !group.mainServiceId) return;
        setLoadingAttendants(true);
        atendentesService
            .getByEstabId(estabId, group.mainServiceId)
            .then((r) => setAttendants(Array.isArray(r.data) ? r.data : []))
            .catch(() => setAttendants([]))
            .finally(() => setLoadingAttendants(false));
    }, [estabId, group.mainServiceId]);

    useEffect(() => {
        if (attendants.length === 1 && !attendantId) {
            setAttendantId(attendants[0]?.uid || '');
        }
    }, [attendants, attendantId]);

    useEffect(() => {
        if (!estabId || !attendantId || !effectiveStartDate) {
            setHorarios([]);
            setSelectedTime(null);
            return;
        }

        let active = true;
        setLoadingTimes(true);

        const dateObj = moment(effectiveStartDate)
            .startOf('day')
            .toDate();
        agendaClienteService
            .getHorarios(
                estabId,
                dateObj,
                group.mainServiceId,
                attendantId,
                group.totalDuration,
            )
            .then((r) => {
                if (!active) return;
                const list = Array.isArray(r.data) ? r.data : [];
                setHorarios(list);
                setSelectedTime((current) => (list.includes(current as number) ? current : null));
            })
            .catch(() => {
                if (!active) return;
                setHorarios([]);
                setSelectedTime(null);
            })
            .finally(() => {
                if (!active) return;
                setLoadingTimes(false);
            });

        return () => {
            active = false;
        };
    }, [
        estabId,
        attendantId,
        effectiveStartDate,
        group.mainServiceId,
        group.totalDuration,
    ]);

    const handlePreviousWeek = () => {
        if (isStartLocked || !effectiveStartDate) return;
        const nextDate = moment(effectiveStartDate)
            .subtract(1, 'week')
            .format('YYYY-MM-DD');
        setSelectedDate(nextDate);
    };

    const handleNextWeek = () => {
        if (isStartLocked || !effectiveStartDate) return;
        const nextDate = moment(effectiveStartDate)
            .add(1, 'week')
            .format('YYYY-MM-DD');
        setSelectedDate(nextDate);
    };

    const validateCreateInputs = (showErrors = true) => {
        if (!clientId || !assinaturaId) {
            if (showErrors) errorSnackbar('Assinatura inválida para criar recorrência.');
            return false;
        }
        if (!attendantId) {
            if (showErrors) errorSnackbar('Selecione um atendente.');
            return false;
        }
        if (!effectiveStartDate) {
            if (showErrors) errorSnackbar('Informe a data do primeiro agendamento.');
            return false;
        }
        if (selectedTime === null) {
            if (showErrors) errorSnackbar('Selecione um horário disponível.');
            return false;
        }
        if (!endDate) {
            if (showErrors) errorSnackbar('Preencha a data final da recorrência.');
            return false;
        }
        if (!moment(endDate).isAfter(moment(effectiveStartDate), 'day')) {
            if (showErrors) errorSnackbar('A data final deve ser posterior à data de início.');
            return false;
        }
        if (tipo === 'meses' && (!monthlyWeekday || !monthlyOccurrence)) {
            if (showErrors) errorSnackbar('Informe o dia da semana e a ocorrência mensal.');
            return false;
        }
        return true;
    };

    const handleCreate = async (): Promise<boolean> => {
        if (!validateCreateInputs(true)) return false;
        const safeStartMinutes = Number(selectedTime);
        const safeClientId = String(clientId || '');

        if (!safeClientId || !Number.isFinite(safeStartMinutes)) {
            errorSnackbar('Dados inválidos para criar a recorrência.');
            return false;
        }

        const recurrenceDiaSemana =
            tipo === 'semanas'
                ? preferredWeekday || getDiaSemana(effectiveStartDate)
                : tipo === 'meses'
                    ? monthlyWeekday || diaSemanaFromDate(effectiveStartDate)
                    : '';

        setSaving(true);
        try {
            const startAt = moment(effectiveStartDate)
                .startOf('day')
                .add(safeStartMinutes, 'minutes')
                .toDate();
            const recorrencia = {
                ativa: true,
                tipo,
                frequencia: Number(frequencia) || 1,
                diaSemana: recurrenceDiaSemana,
                modoMensal: tipo === 'meses' ? 'diaSemana' : undefined,
                ocorrenciaMensal: tipo === 'meses' ? monthlyOccurrence : undefined,
                diaMes: tipo === 'meses' ? moment(effectiveStartDate).date() : undefined,
                inicio: moment(effectiveStartDate).format('YYYY-MM-DD'),
                fim: moment(endDate).format('YYYY-MM-DD'),
            };

            await horariosAtendenteService.createAgendamento({
                startAt,
                localDate: moment(effectiveStartDate).format('YYYY-MM-DD'),                servicoId: group.mainServiceId,
                servicosExtrasIds: group.extrasIds,
                attendantId,
                startMinutes: safeStartMinutes,
                clientId: safeClientId,
                recorrencia,
                duration: group.totalDuration,
                assinaturaId,
                observation: '',
            });

            successSnackbar('Recorrência criada com sucesso!');
            onCreated();
            return true;
        } catch (e: any) {
            const message = e?.response?.data?.message;
            errorSnackbar(
                message === 'conflictHours'
                    ? 'Este horário já está ocupado. Escolha outro horário disponível.'
                    : message === 'attendantServiceMismatch'
                        ? 'O atendente selecionado não atende um ou mais serviços do plano.'
                        : 'Não foi possível criar a recorrência.',
            );
            return false;
        } finally {
            setSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        create: handleCreate,
        isReady: () => validateCreateInputs(false),
    }));

    return (
        <div className="space-y-5">
            <SettingsSection
                title="Serviços"
                footer={`Duração total: ${group.totalDuration} minutos. O primeiro serviço é o principal e os demais entram como extras.`}
            >
                <div className="ass-note !text-base-content">{group.serviceNames.join(' + ')}</div>
            </SettingsSection>

            <section>
                <h3 className="estab-section-header">Atendente</h3>
                <div className="estab-group">
                    {loadingAttendants ? (
                        <div className="ass-attendants" aria-busy="true">
                            {[0, 1, 2].map((item) => (
                                <div key={item} className="sf-skeleton h-20 w-20 shrink-0" />
                            ))}
                        </div>
                    ) : attendants.length === 0 ? (
                        <div className="ass-note">Nenhum atendente disponível para estes serviços.</div>
                    ) : (
                        <div className="ass-attendants" role="group" aria-label="Atendente">
                            {attendants.map((a) => (
                                <button
                                    key={a.uid}
                                    type="button"
                                    className="ass-attendant"
                                    aria-pressed={attendantId === a?.uid}
                                    onClick={() => setAttendantId(a?.uid === attendantId ? '' : a?.uid)}
                                >
                                    <img src={`${baseURL}/users/profileImage/${a?.uid}`} alt="" />
                                    <span>{a?.displayName || a?.email || 'Atendente'}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section>
                <h3 className="estab-section-header">Primeiro agendamento</h3>
                <div className="estab-group">
                    <div className="ass-week-bar">
                        <span className="first-letter:uppercase">{formattedDate}</span>
                        <span className="flex shrink-0 items-center">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={handlePreviousWeek}
                                aria-label="Semana anterior"
                            >
                                <ChevronLeft aria-hidden />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={handleNextWeek}
                                aria-label="Próxima semana"
                            >
                                <ChevronRight aria-hidden />
                            </button>
                        </span>
                    </div>

                    <div className="ass-days" role="group" aria-label="Dia">
                        {weekDays.map((day) => (
                            <button
                                key={day.format('YYYY-MM-DD')}
                                type="button"
                                className="ass-day"
                                aria-pressed={!!effectiveStartDate && day.isSame(moment(effectiveStartDate), 'day')}
                                aria-label={day.format('dddd, DD/MM')}
                                onClick={() => setSelectedDate(day.format('YYYY-MM-DD'))}
                            >
                                <span>{day.format('ddd').replace('.', '')}</span>
                                {day.format('DD')}
                            </button>
                        ))}
                    </div>

                    {!attendantId ? (
                        <div className="ass-note !pt-0">Selecione um atendente para ver os horários.</div>
                    ) : loadingTimes ? (
                        <div className="ass-times !pt-0" aria-busy="true">
                            {Array.from({ length: 8 }).map((_, idx) => (
                                <div key={idx} className="sf-skeleton h-9" />
                            ))}
                        </div>
                    ) : horarios.length === 0 ? (
                        <div className="ass-note !pt-0">Nenhum horário disponível neste dia.</div>
                    ) : (
                        <div className="ass-times !pt-0" role="group" aria-label="Horário">
                            {horarios.map((h) => (
                                <button
                                    key={h}
                                    type="button"
                                    className="ass-time"
                                    aria-pressed={selectedTime === h}
                                    onClick={() => setSelectedTime(h)}
                                >
                                    <DurationService duration={h} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <SettingsSection
                title="Repetição"
                footer={
                    describeRegra({
                        tipo,
                        frequencia,
                        diaSemana: tipo === 'semanas' ? preferredWeekday : monthlyWeekday,
                        modoMensal: tipo === 'meses' ? 'diaSemana' : undefined,
                        ocorrenciaMensal: tipo === 'meses' ? monthlyOccurrence : undefined,
                    }) + '.'
                }
            >
                {tipo === 'semanas' && (
                    <SettingsRow label="Dia da semana" htmlFor="recorrencia-dia">
                        <select
                            id="recorrencia-dia"
                            className="select estab-field"
                            value={preferredWeekday}
                            onChange={(e) => {
                                setPreferredWeekdayTouched(true);
                                setPreferredWeekday(e.target.value);
                            }}
                        >
                            {diasSemanaOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </SettingsRow>
                )}

                {tipo === 'meses' && (
                    <>
                        <SettingsRow label="Dia da semana" htmlFor="recorrencia-dia">
                            <select
                                id="recorrencia-dia"
                                className="select estab-field"
                                value={monthlyWeekday}
                                onChange={(e) => {
                                    setMonthlyRuleTouched(true);
                                    setMonthlyWeekday(e.target.value as DiaSemana);
                                }}
                            >
                                {diasSemanaOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </SettingsRow>
                        <SettingsRow label="Ocorrência no mês" htmlFor="recorrencia-ocorrencia">
                            <select
                                id="recorrencia-ocorrencia"
                                className="select estab-field"
                                value={monthlyOccurrence}
                                onChange={(e) => {
                                    setMonthlyRuleTouched(true);
                                    setMonthlyOccurrence(e.target.value as OcorrenciaMensal);
                                }}
                            >
                                {ocorrenciasMensaisOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </SettingsRow>
                    </>
                )}

                <SettingsRow label="Início" htmlFor="recorrencia-inicio">
                    <input
                        id="recorrencia-inicio"
                        type="date"
                        className="input estab-field"
                        value={effectiveStartDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />
                </SettingsRow>
                <SettingsRow
                    label="Fim"
                    htmlFor="recorrencia-fim"
                    description={isEndLocked ? 'Definido pela assinatura.' : undefined}
                >
                    <input
                        id="recorrencia-fim"
                        type="date"
                        className="input estab-field"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isEndLocked}
                    />
                </SettingsRow>
            </SettingsSection>
        </div>
    );
});

RecorrenciaGroupCard.displayName = 'RecorrenciaGroupCard';

export default function AssinaturasRecorrencias({ assinatura }: { assinatura: any }) {
    const { errorSnackbar, successSnackbar } = useAppContext();
    const { estabSession } = useEstabsContext();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 800 });
    const id: string = assinatura?._id;

    const [loading, setLoading] = useState(true);
    const [recorrencias, setRecorrencias] = useState<any[]>([]);
    const [servicesDict, setServicesDict] = useState<Record<string, ServicoInfo>>({});
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creatingRecurrence, setCreatingRecurrence] = useState(false);
    const [recorrenciaToRemove, setRecorrenciaToRemove] = useState<any>(null);
    const [removing, setRemoving] = useState(false);
    const recurrenceRefs = useRef<Record<string, RecorrenciaGroupCardHandle | null>>({});

    const loadServicos = async () => {
        try {
            const servs = await servicosService.findAll().then((r: any) => r.data || []);
            const dict: Record<string, ServicoInfo> = {};
            (servs || []).forEach((s: any) => {
                if (s?._id) {
                    dict[s._id] = { name: s.name, duration: Number(s.duration) || 0 };
                }
            });
            setServicesDict(dict);
        } catch (e) {
            console.error('Erro ao carregar serviços:', e);
        }
    };

    const loadRecorrencias = async () => {
        if (!id) return;
        try {
            const recs = await recorrenciasService.getByAssinaturaId(id).then((r: any) => r.data || []);
            setRecorrencias(Array.isArray(recs) ? recs : []);
        } catch (e) {
            console.error('Erro ao carregar recorrências:', e);
            errorSnackbar('Falha ao carregar as recorrências.');
        }
    };

    const fetchAll = async () => {
        if (!id) return;
        try {
            setLoading(true);
            await Promise.all([loadServicos(), loadRecorrencias()]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const recurrenceGroups = useMemo(() => {
        const plan =
            assinatura?.planoSnapshot ||
            assinatura?.plano ||
            assinatura?.plan;
        const list: PlanoService[] = plan?.services || [];
        const services = list.filter((s) => s?.serviceId);

        if (!services.length) {
            return [];
        }

        const [mainService, ...extraServices] = services;
        const serviceIds = services.map((s) => s.serviceId);
        const recurrence = normalizeRecurrence(mainService.recurrence);
        const totalDuration = serviceIds.reduce(
            (acc, id) => acc + Number(servicesDict[id]?.duration || 0),
            0,
        );

        return [
            {
                key: 'assinatura-completa',
                recurrence,
                quantity: Number(mainService.quantity) || 1,
                defaultFrequency: 1,
                frequencyTipo: recurrence === 'semanal' ? 'semanas' : 'meses',
                serviceIds,
                mainServiceId: mainService.serviceId,
                extrasIds: extraServices.map((s) => s.serviceId),
                serviceNames: serviceIds.map((serviceId) => servicesDict[serviceId]?.name || 'Serviço'),
                totalDuration,
            },
        ] as RecorrenciaGroup[];
    }, [assinatura, servicesDict]);

    const planConfig = useMemo(
        () => resolveAssinaturaPlanConfig(assinatura),
        [assinatura],
    );

    const defaultStartAt = assinatura?.startAt
        ? zonedDate(assinatura.startAt, estabSession?.timezone).format('YYYY-MM-DD')
        : zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD');

    const computedEndAt = useMemo(() => {
        if (assinatura?.endAt) {
            const endAt = new Date(assinatura.endAt);
            return Number.isNaN(endAt.getTime()) ? null : endAt;
        }
        return computeEndAtFromConfig(assinatura?.startAt, planConfig, estabSession?.timezone);
    }, [assinatura, planConfig, estabSession?.timezone]);

    const defaultEndAt = computedEndAt
        ? zonedDate(computedEndAt, estabSession?.timezone).format('YYYY-MM-DD')
        : '';

    const handleCreateRecurrence = async () => {
        const recurrence = recurrenceGroups[0];
        const card = recurrence ? recurrenceRefs.current[recurrence.key] : null;

        if (!recurrence || !card) {
            return;
        }

        setCreatingRecurrence(true);
        try {
            // `create` valida os campos e informa o que falta ou o erro da API.
            const created = await card.create();

            if (created) {
                await loadRecorrencias();
                setShowCreateForm(false);
            }
        } finally {
            setCreatingRecurrence(false);
        }
    };

    const handleRemoveRecorrencia = async () => {
        if (!recorrenciaToRemove) return;

        try {
            setRemoving(true);
            await recorrenciasService.deleteRecorrencia(recorrenciaToRemove._id);
            await loadRecorrencias();
            successSnackbar('Recorrência removida.');
            setRecorrenciaToRemove(null);
        } catch (e) {
            errorSnackbar('Erro ao remover a recorrência.');
        } finally {
            setRemoving(false);
        }
    };

    const createFormContent = loading ? (
        <div className="space-y-3" aria-busy="true">
            <div className="sf-skeleton h-24" />
            <div className="sf-skeleton h-40" />
        </div>
    ) : recurrenceGroups.length === 0 ? (
        <div className="sf-state">
            <Scissors aria-hidden />
            <h2>Nenhum serviço no plano</h2>
        </div>
    ) : (
        <div className="estab-settings estab-settings--sheet">
            {recurrenceGroups.map((group) => (
                <RecorrenciaGroupCard
                    key={group.key}
                    ref={(instance) => {
                        recurrenceRefs.current[group.key] = instance;
                    }}
                    group={group}
                    assinaturaId={assinatura?._id}
                    clientId={assinatura?.clienteId}
                    estabId={estabSession?._id}
                    defaultStartAt={defaultStartAt}
                    defaultEndAt={defaultEndAt}
                    onCreated={() => undefined}
                    showCreateButton={false}
                />
            ))}

            <div className="ass-sheet-actions">
                <button
                    type="button"
                    className="btn btn-primary w-full sm:w-auto sm:min-w-[12rem]"
                    disabled={creatingRecurrence}
                    onClick={handleCreateRecurrence}
                >
                    {creatingRecurrence && <span className="loading loading-spinner loading-sm"></span>}
                    Criar recorrência
                </button>
            </div>
        </div>
    );

    return (
        <div>
            <div className="ass-section-head">
                <h3 className="sf-group-title">Recorrências vinculadas</h3>
                {assinatura?.status === 1 && (
                    <button type="button" className="sf-link" onClick={() => setShowCreateForm(true)}>
                        <Plus aria-hidden />
                        Adicionar
                    </button>
                )}
            </div>

            {loading ? (
                <div className="space-y-2" aria-busy="true">
                    <div className="sf-skeleton h-16" />
                    <div className="sf-skeleton h-16" />
                </div>
            ) : recorrencias.length === 0 ? (
                <div className="sf-group">
                    <div className="sf-state">
                        <RefreshCw aria-hidden />
                        <h2>Nenhuma recorrência</h2>
                        <p>Crie uma recorrência para agendar automaticamente os serviços do plano.</p>
                    </div>
                </div>
            ) : (
                <ul className="sf-group">
                    {recorrencias.map((rec) => {
                        const proximo = rec.resumo?.proximoStartAt;

                        return (
                            <li key={rec._id} className="ass-recurrence">
                                <button
                                    type="button"
                                    className="ass-recurrence-main"
                                    onClick={() => navigate(`/recorrencias/${rec._id}`)}
                                >
                                    <span className="ass-title block">{describeRegra(rec)}</span>
                                    <span className="ass-sub">
                                        {(rec.serviceNames?.length ? rec.serviceNames : [rec.servicoName]).filter(Boolean).join(' + ') || 'Serviço'}
                                        {rec.attendantName ? ` · ${rec.attendantName}` : ''}
                                    </span>
                                    <span className="ass-sub">
                                        {proximo
                                            ? `Próximo: ${formatProximo(proximo, estabSession?.timezone)}`
                                            : `Até ${formatCivilDateBR(rec.endAt)}`}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className="ass-icon-button"
                                    onClick={() => setRecorrenciaToRemove(rec)}
                                    aria-label="Remover recorrência"
                                    title="Remover recorrência"
                                >
                                    <Trash2 aria-hidden />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {recorrenciaToRemove && (
                <Modal
                    title="Remover recorrência?"
                    onClose={() => setRecorrenciaToRemove(null)}
                    desktopClassName="sm:!max-w-md"
                >
                    <p className="text-sm text-base-content/70">
                        Os agendamentos em aberto desta recorrência serão cancelados e os horários, liberados. Os
                        atendimentos concluídos continuam no histórico.
                    </p>
                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setRecorrenciaToRemove(null)}
                            disabled={removing}
                        >
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-error" onClick={handleRemoveRecorrencia} disabled={removing}>
                            {removing && <span className="loading loading-spinner loading-sm"></span>}
                            Remover
                        </button>
                    </div>
                </Modal>
            )}

            {showCreateForm && (
                isMobile ? (
                    <Modal onClose={() => setShowCreateForm(false)} title="Nova recorrência">
                        {createFormContent}
                    </Modal>
                ) : (
                    <Drawer
                        onClose={() => setShowCreateForm(false)}
                        title="Nova recorrência"
                        widthClass="w-full max-w-2xl"
                    >
                        {createFormContent}
                    </Drawer>
                )
            )}
        </div>
    );
}
